/**
 * §12's service capture and §15's two legal clocks — the copy, and the two calculations the
 * web is allowed to make.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS FILE MAY AND MAY NOT COMPUTE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **No deadline is computed here, and none may be.** `packages/shared/src/hearing-clocks.ts`
 * holds `deriveAppealWindow`, `describeAppealWindow`, `appealMinutesRemaining`,
 * `isAppealWindowClosed` and `deriveLateReportWindow`, and its header gives the reason: a
 * deadline is derived from the service it counts from, in exactly one place, or it drifts from
 * it. Everything below either FORMATS a value those functions returned, or turns what the TD
 * typed into the instant the API demands.
 *
 * The one real calculation is {@link toOffsetInstant}, and it is a calculation about the
 * calendar rather than about the law: the TD types `21/08/2026` and `16:05` because that is
 * what the signed `RECEIVED:` line says, and the route refuses anything without a timezone
 * offset — correctly, since `16:05` is not an instant until somebody says where. The offset is
 * read from `Intl` for the tournament's own zone at that date, never hard-coded, because a
 * hard-coded `+09:00` is a bug the first time this system is used anywhere else.
 *
 * British English throughout.
 */

import type { AppealWindow, LateReportWindow } from "@hockeytd/shared";

import type { HearingServiceCorrectionView } from "../../api/types.js";

/* ────────────────────────────────────────────────────────────────────────────
 * Composing the instant the API demands
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The zone's offset from UTC at one instant, in whole minutes.
 *
 * `Intl` is the only thing in the browser that knows what `Asia/Tokyo` was doing on a given
 * day, so the offset is read from it rather than assumed. Japan keeps no summer time, which is
 * exactly why this must not be written as a constant: the next tournament may be somewhere
 * that does, and a fixed `+09:00` would then place service an hour from where the TD put it.
 */
export function zoneOffsetMinutes(at: Date, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(at);

  const field = (type: string): number => Number(parts.find((part) => part.type === type)?.value);
  // `formatToParts` renders hour 24 for midnight under some ICU builds; `Date.UTC` normalises.
  const asUtc = Date.UTC(
    field("year"),
    field("month") - 1,
    field("day"),
    field("hour") % 24,
    field("minute"),
    field("second"),
  );
  return Math.round((asUtc - at.getTime()) / 60_000);
}

/** `+09:00`, or `-04:30`, from whole minutes. */
export function offsetLabel(minutes: number): string {
  const sign = minutes < 0 ? "-" : "+";
  const absolute = Math.abs(minutes);
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${sign}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`;
}

/**
 * `2026-08-21` + `16:05` in the tournament's zone → `2026-08-21T16:05:00+09:00`.
 *
 * Returns null when either half is missing or malformed, so the caller can refuse to submit
 * rather than send the route something it will reject. The offset is resolved twice: once
 * against the naive guess and once against the resulting instant, so a service recorded within
 * an hour of a clock change lands on the offset actually in force at it.
 */
export function toOffsetInstant(date: string, time: string, timezone: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  if (!/^\d{2}:\d{2}(?::\d{2})?$/.test(time)) return null;

  const clock = time.length === 5 ? `${time}:00` : time;
  const naive = Date.parse(`${date}T${clock}Z`);
  if (Number.isNaN(naive)) return null;

  const firstPass = zoneOffsetMinutes(new Date(naive), timezone);
  const settled = zoneOffsetMinutes(new Date(naive - firstPass * 60_000), timezone);
  return `${date}T${clock}${offsetLabel(settled)}`;
}

/** `2026-08-21T07:05:00Z` → `2026-08-21` in the tournament's zone, for the date input. */
export function zonedDateValue(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
  return parts;
}

/** `2026-08-21T07:05:00Z` → `16:05` in the tournament's zone, for the time input. */
export function zonedTimeValue(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

/** `Fri 21 Aug 2026, 16:05` — the recorded instant, to the minute, in the tournament's zone. */
export function serviceInstantText(iso: string, timezone: string): string {
  const day = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
  return `${day}, ${zonedTimeValue(iso, timezone)}`;
}

/* ────────────────────────────────────────────────────────────────────────────
 * §12 — the service form
 * ──────────────────────────────────────────────────────────────────────────── */

/** Which batches may be served, and what §12 and §10 call the act on each. */
export const SERVICE_BATCH_HEADING: Readonly<Record<1 | 3, string>> = {
  1: "Service Of The Hearing Letter",
  3: "Service Of The Decision Letter",
};

/**
 * What service on each letter means, said plainly, because they mean different things.
 *
 * §12 is explicit that the appeal period runs from service of the DECISION. §10's letter is
 * served too, and recording it evidences that the person was told the case before it was heard
 * — §2's second principle of natural justice — and it starts no clock at all. A screen that
 * drew the same control twice without saying so would invite a TD to read a countdown into
 * batch 1's service, which is the one thing this surface exists not to do.
 */
export const SERVICE_BATCH_HINT: Readonly<Record<1 | 3, string>> = {
  1:
    "Record when the Hearing Letter was handed over and who signed for it. This evidences " +
    "that the person was told the case before it was heard. It starts no appeal window.",
  3:
    "Record the date and time on the signed RECEIVED: block and who signed for it. The " +
    "appeal period runs from service of this decision, so this instant is the one the " +
    "appeal window is counted from.",
};

/** §11: the minutes are filed to the case, not served on anybody. Said where it is not drawn. */
export const SERVICE_NOT_ON_MINUTES =
  "The minutes are filed to the case, not served on anybody, so there is nothing to record here.";

export const SERVICE_UNISSUED_HINT =
  "Service can be recorded once this document has been issued. A document that has not gone " +
  "out has not been served.";

/**
 * WHAT SERVICE IS, IN ONE LINE, ON THE PANEL ITSELF.
 *
 * The Technical Delegate met `service cannot precede the document being issued` and could not
 * tell from the screen what service was, when it could be recorded or why the system was
 * refusing him. Every word of that was on the page somewhere — in a hint under a button he
 * had not pressed, in a heading, in a walkthrough — and none of it was where he was looking.
 * So the whole of it is said once, plainly, above the control, on both letters.
 */
export const SERVICE_EXPLAINER =
  "Service = recording when the letter was physically handed to the Team Manager and who " +
  "signed for it. You can only record it after the letter has been issued (Draft → Issue). " +
  "Later, the Decision Letter's service starts the appeal clock.";

/** On the greyed-out button itself, as the reason it is greyed out. */
export const SERVICE_DISABLED_REASON =
  "Issue the letter first — a letter that has not gone out has not been served.";

/**
 * The local mirror of migration 033's `service cannot precede the document being issued`.
 *
 * It is the same rule, refused a moment earlier and with the instant named, so the TD is told
 * what the earliest permissible time IS rather than only that his answer was wrong. The
 * server's refusal is untouched and is still the one that decides: this cannot admit anything
 * the database would refuse, only refuse it sooner.
 */
export function serviceBeforeIssueRefusal(issuedAt: string, timezone: string): string {
  return (
    `Service cannot be earlier than the moment the letter was issued — ` +
    `${serviceInstantText(issuedAt, timezone)}. Give the date and time it was actually handed over.`
  );
}

export const SERVICE_TIMEZONE_HINT =
  "Date and time as written on the signed paper, in the tournament's own time zone. It is " +
  "sent with that zone's offset — a bare local time is refused, because this instant starts a " +
  "legal deadline.";

/*
 * THE SENTENCES BELOW USED TO PROMISE SOMETHING NOTHING KEPT - AND NOW PROMISE IT ONLY WHERE IT IS.
 *
 * Each said the case history held the reason and the instant a correction moved from. Until
 * migration 066 nothing did: the reason was checked and discarded, the old instant was
 * overwritten, and the chain row said only that a correction happened. The Technical Delegate's
 * Decision #6 = A (19 Sep 2026) made the promise true rather than the sentences quieter, so they
 * say what is kept and WHERE it is shown - the letter's own Service History.
 *
 * BUT ONLY AN API THAT HAS MIGRATION 066 KEEPS IT, and it says so by sending `serviceCorrections` on
 * every document of the case file. This bundle can meet an API without it, and not only for a
 * moment: `infra/deploy.sh` ships the bundle before it restarts the API, and a rollback
 * (`infra/RUNBOOK.md` section 6) swaps the API back while leaving the newest bundle in place for as
 * long as nobody redeploys. So every sentence that promises the record has a second version that
 * promises nothing, and `serviceWording()` picks between them from the case file itself (migration
 * 066's first review round, security L1 and backend L8).
 */

export const SERVICE_CORRECTION_HINT =
  "Service is already recorded on this document, so this is a correction. The reason you give " +
  "is kept with the time and the name it replaces, and every correction is listed under " +
  "Service History on this letter.";

export const SERVICE_WITHDRAW_HINT =
  "Withdraw removes the recorded service and stops the appeal window. It is the remedy for " +
  "service logged against the wrong case. Service History on this letter still shows what was " +
  "claimed, and why it was withdrawn.";

/** The refusals this form makes itself, before the route is troubled. */
export const SERVICE_LOCAL_REFUSAL = {
  instant: "Enter both the date and the time service was effected.",
  servedOn: "Name who service was effected on — the person who signed for it.",
  reason:
    "State the reason for the correction. It is kept with the correction and shown under " +
    "Service History.",
  withdrawReason: "State the reason for withdrawing the recorded service.",
} as const;

/**
 * Under the reason field. The reason cannot be edited or removed once it is saved (migration 066's
 * table refuses every UPDATE and DELETE), it stays with the case record for as long as the case is
 * kept, and a case record is what an appeal reads - so it is no place for somebody's health, or
 * anything else about a person that the record does not need (fix round 1, security L5).
 */
export const SERVICE_REASON_PRIVACY_HINT =
  "The reason is kept with the case record and cannot be edited or removed, and it is shown on " +
  "an appeal - so leave out health or other personal details.";

/** Said after "Corrected in this session - it previously read {instant}." */
export const SERVICE_CORRECTED_NOTE =
  "Both statements and the reason given are kept, and are listed under Service History below.";

/** The two notices' words, after the heading the panel prints before them. */
export const SERVICE_CORRECTED_NOTICE =
  "The time it moved from and the reason you gave are kept, and listed under Service History " +
  "on this letter.";

export const SERVICE_WITHDRAWN_NOTICE =
  "The recorded service is removed. What it said and the reason you gave are kept, and listed " +
  "under Service History on this letter.";

/** Every sentence the form and the panel say about a correction, in one version. */
export interface ServiceWording {
  correctionHint: string;
  withdrawHint: string;
  /** The form's own refusal of a correction with no reason. */
  reasonRefusal: string;
  reasonPrivacyHint: string;
  /** After "Corrected in this session - it previously read {instant}."; empty when nothing is promised. */
  correctedNote: string;
  correctedNotice: string;
  withdrawnNotice: string;
}

const SERVICE_WORDING_WITH_HISTORY: ServiceWording = {
  correctionHint: SERVICE_CORRECTION_HINT,
  withdrawHint: SERVICE_WITHDRAW_HINT,
  reasonRefusal: SERVICE_LOCAL_REFUSAL.reason,
  reasonPrivacyHint: SERVICE_REASON_PRIVACY_HINT,
  correctedNote: SERVICE_CORRECTED_NOTE,
  correctedNotice: SERVICE_CORRECTED_NOTICE,
  withdrawnNotice: SERVICE_WITHDRAWN_NOTICE,
};

/**
 * THE SAME PLACES, PROMISING NOTHING - for a case file whose documents carry no
 * `serviceCorrections`. Such an API may or may not have kept the record (after a rollback, 066 is
 * still applied and the old API simply reads none of it), and this screen cannot tell which - so
 * each sentence says what the act does, and nothing about what is kept or where.
 */
export const SERVICE_WORDING_WITHOUT_HISTORY: ServiceWording = {
  correctionHint:
    "Service is already recorded on this document, so this is a correction, and it needs a reason.",
  withdrawHint:
    "Withdraw removes the recorded service and stops the appeal window. It is the remedy for " +
    "service logged against the wrong case.",
  reasonRefusal: "State the reason for the correction.",
  reasonPrivacyHint: "Leave out health or other personal details.",
  correctedNote: "",
  correctedNotice: "The recorded service now reads as you gave it.",
  withdrawnNotice: "The recorded service is removed.",
};

/** `historyKept` is true exactly when the case file carried `serviceCorrections` for the letter. */
export function serviceWording(historyKept: boolean): ServiceWording {
  return historyKept ? SERVICE_WORDING_WITH_HISTORY : SERVICE_WORDING_WITHOUT_HISTORY;
}

/* ----------------------------------------------------------------------------
 * Section 12a - the service history (migration 066, Decision #6 = A)
 * ---------------------------------------------------------------------------- */

export const SERVICE_HISTORY_HEADING = "Service History";

/**
 * One line of a letter's Service History, in parts - because three of them are TYPED TEXT (the
 * two recipients and the reason) and one is a roster name, and the screen draws each of those
 * inside its own bidi isolate, so no character in one of them can reorder the words around it.
 */
export interface ServiceHistoryLine {
  /** The instant it moved FROM, in the tournament's zone. */
  fromAt: string;
  fromOn: string;
  /** The instant it moved TO; null for a withdrawal. */
  toAt: string | null;
  toOn: string | null;
  /** The Technical Delegate's reason, verbatim. */
  reason: string;
  /** Who made the correction - a roster media name - and when, in the tournament's zone. */
  who: string;
  when: string;
}

/**
 * `Moved from Wed 23 Sep 2026, 14:05 (on X) to Wed 23 Sep 2026, 16:05 (on Y)` or `Withdrawn: it
 * read Wed 23 Sep 2026, 14:05 (on X)`, then the reason, then who and when - every instant in the
 * tournament's zone, by {@link serviceInstantText}, like every other instant on the service block.
 *
 * A withdrawal says what it withdrew. The document itself reads "No service is recorded" once
 * service is withdrawn, so a line that said only "Withdrawn" would leave the claim that was taken
 * back - the thing a corrected record exists to keep - nowhere on the screen.
 *
 * The reason is printed as the Technical Delegate typed it and is never reworded.
 */
export function serviceHistoryLine(
  correction: HearingServiceCorrectionView,
  timezone: string,
): ServiceHistoryLine {
  return {
    fromAt: serviceInstantText(correction.fromServedAt, timezone),
    fromOn: correction.fromServedOn,
    toAt: correction.toServedAt === null ? null : serviceInstantText(correction.toServedAt, timezone),
    toOn: correction.toServedOn,
    reason: correction.reason,
    who: correction.actorMediaName,
    when: serviceInstantText(correction.at, timezone),
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * §15's second clock — the appeal window
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The remedy for each basis, beside `describeAppealWindow`'s own sentence.
 *
 * `hearing-clocks.ts` insists the two absent cases are different facts, and they are different
 * facts because they have different remedies: one is answered by recording service when it
 * happens, the other by a Configuration change nobody at the desk can make from this screen.
 */
export const APPEAL_BASIS_REMEDY = {
  not_served:
    "No appeal window is running. Record service above once the decision has been handed " +
    "over and signed for, and the countdown will run from that instant.",
  not_configured:
    "Service is recorded, so an appeal window is running — but this tournament has no " +
    "appeal window configured, so nobody can say when it ends. Set it in Tournament " +
    "Configuration.",
  served:
    "Counted from the recorded service instant and this tournament's configured appeal " +
    "window. Check it against the signed paper in the case folder.",
} as const;

/** Said when the appeal window setting could not be read at all — not the same as unset. */
export const APPEAL_SETTINGS_UNREAD =
  "The appeal window setting could not be read, so no countdown is shown. This is not a " +
  "statement that none is configured — try again, or check Tournament Configuration " +
  "directly.";

export const APPEAL_HEADING = "Appeal Window";

/** §12, quoted where the clock is drawn, so the screen says what it is counting. */
export const APPEAL_AUTHORITY = "The appeal period runs from service of this decision.";

/**
 * The countdown, as words.
 *
 * `appealMinutesRemaining` returns negatives deliberately — a window that closed forty minutes
 * ago is a different fact from one closing now — so a closed window is rendered as CLOSED with
 * how long ago, and never as `0m` sitting there for the rest of the tournament.
 */
export function appealCountdownText(minutesRemaining: number, closed: boolean): string {
  if (closed) {
    const elapsed = Math.abs(minutesRemaining);
    if (elapsed < 1) return "Closed — just now.";
    return `Closed — ${durationWords(elapsed)} ago.`;
  }
  if (minutesRemaining < 1) return "Closes in under a minute.";
  return `${durationWords(minutesRemaining)} remaining.`;
}

/** `1 hour 4 minutes`, `47 minutes`, `2 days 3 hours`. Whole units, no false precision. */
export function durationWords(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const days = Math.floor(whole / 1440);
  const hours = Math.floor((whole % 1440) / 60);
  const mins = whole % 60;
  const plural = (value: number, noun: string): string =>
    `${String(value)} ${noun}${value === 1 ? "" : "s"}`;

  if (days > 0) return `${plural(days, "day")} ${plural(hours, "hour")}`;
  if (hours > 0) return `${plural(hours, "hour")} ${plural(mins, "minute")}`;
  return plural(mins, "minute");
}

/** The chip's tone: an open window is attention, a closed one is settled fact. */
export function appealPillClass(window: AppealWindow, closed: boolean): string {
  if (window.basis !== "served") return "statuspill statuspill--muted";
  return closed ? "statuspill" : "statuspill statuspill--warn";
}

/** The chip's word. Short, and never a number that could be read as a deadline. */
export function appealPillText(window: AppealWindow, closed: boolean): string {
  switch (window.basis) {
    case "not_served":
      return "Not Served";
    case "not_configured":
      return "Window Not Configured";
    case "served":
      return closed ? "Closed" : "Open";
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * §15's first clock — the 24-hour report window
 * ──────────────────────────────────────────────────────────────────────────── */

export const LATE_REPORT_HEADING = "Report Window";

/**
 * §15's countdown chip, from `deriveLateReportWindow`.
 *
 * It is measured against the REPORT, not against the clock — a case whose report arrived in
 * time does not become late by being looked at two days later — so a case with a report on it
 * states the fact rather than counting anything, and only a case still waiting for one counts.
 */
export function lateReportChipText(
  window: LateReportWindow,
  reportReceived: boolean,
): string {
  const threshold = `${String(window.thresholdHours)}-hour`;
  if (reportReceived) {
    return window.isLate
      ? `Late — the report arrived after the ${threshold} window closed.`
      : `In time — the report arrived within the ${threshold} window.`;
  }
  if (window.hoursRemaining < 0) {
    return `The ${threshold} window closed ${durationWords(Math.abs(window.hoursRemaining) * 60)} ago.`;
  }
  return `${durationWords(window.hoursRemaining * 60)} left of the ${threshold} window.`;
}

/** The chip's tone. A late report is a hard fact the letter must carry; in time is advisory. */
export function lateReportPillClass(isLate: boolean): string {
  return isLate ? "statuspill statuspill--alert" : "statuspill statuspill--good";
}
